/**
 * Quick Add — Restyled
 * Mobile: + button always visible → bottom sheet overlay
 * Desktop (≥750px): size bar rising on image hover, adds directly to cart
 */

(function () {
  'use strict';

  const BREAKPOINT = 750;

  /* ── Add to cart — mirrors Prestige's ProductForm exactly ──────────
     Prestige's flow:
     1. Dispatch 'cart:prepare-bundled-sections' on documentElement
        so other components can register sections they need re-rendered
     2. POST to /cart/add.js with those section IDs in formData
     3. Fetch /cart.js to get updated cart state
     4. Dispatch 'cart:change' on documentElement with cart + sections
  ─────────────────────────────────────────────────────────────────── */
  async function addToCart(variantId, button, card) {
    const origText = button.textContent;
    button.classList.add('is-loading');
    button.disabled = true;

    try {
      // Step 1 — collect sections Prestige wants bundled
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      // Step 2 — POST to /cart/add.js with sections
      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', 1);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const addRes = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }

      const responseJson = await addRes.json();

      // Step 3 — fetch updated cart state (same as Prestige)
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      // Step 4 — dispatch cart:change on documentElement (same event Prestige fires)
      const items = responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson];

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: 'force_open_drawer',
          cart: cartContent
        }
      }));

      // Also dispatch variant:add on the card's form if present
      // (some Prestige listeners key on this)
      const cardForm = card?.querySelector('form');
      if (cardForm) {
        cardForm.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items,
            cart: cartContent,
            onSuccessDo: 'force_open_drawer'
          }
        }));
      }

      button.textContent = 'Added ✓';
      button.classList.remove('is-loading');
      button.classList.add('is-success');

      setTimeout(() => {
        button.textContent = origText;
        button.classList.remove('is-success');
        button.disabled = false;
      }, 1800);

    } catch (err) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      button.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = origText;
      console.error('[QuickAdd] Add to cart failed:', err.message);
    }
  }

  /* ── Fetch product data ─────────────────────────────────────────── */
  const variantCache = {};

  async function fetchProduct(handle) {
    const cleanHandle = handle.split('?')[0];
    if (variantCache[cleanHandle]) return variantCache[cleanHandle];
    try {
      const res = await fetch(`/products/${cleanHandle}.js`);
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      variantCache[cleanHandle] = data;
      return data;
    } catch (err) {
      console.error('[QuickAdd] Could not fetch product:', cleanHandle, err);
      return null;
    }
  }

  /* ── Option helpers ─────────────────────────────────────────────── */
  const SIZE_LABELS = ['size', 'taille', 'größe', 'maat', 'tamaño', 'størrelse', 'misura', 'tamanho'];

  function getOptionName(o) {
    return typeof o === 'string' ? o : (o && o.name ? o.name : '');
  }

  function getSizeOptionIndex(product) {
    if (!Array.isArray(product.options)) return null;
    const idx = product.options.findIndex(o =>
      SIZE_LABELS.includes(getOptionName(o).toLowerCase())
    );
    return idx >= 0 ? idx : null;
  }

  function getOrderedSizes(product) {
    const idx = getSizeOptionIndex(product);
    if (idx === null) return [];
    const opt = product.options[idx];
    if (opt && Array.isArray(opt.values)) return opt.values;
    const seen = new Set();
    const sizes = [];
    for (const v of product.variants) {
      const s = v.options[idx];
      if (s !== undefined && !seen.has(s)) { seen.add(s); sizes.push(s); }
    }
    return sizes;
  }

  function buildSizeMap(product) {
    const idx = getSizeOptionIndex(product);
    if (idx === null) return null;
    const map = {};
    for (const v of product.variants) {
      const size = v.options[idx];
      if (size === undefined) continue;
      if (!map[size]) {
        map[size] = { variantId: v.id, available: v.available };
      } else if (!map[size].available && v.available) {
        map[size] = { variantId: v.id, available: v.available };
      }
    }
    return map;
  }

  function handleFromUrl(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^?#/]+)/);
    return match ? match[1] : null;
  }

  function getActiveHandle(card) {
    const checkedRadio = card.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const url = checkedRadio.getAttribute('data-product-url');
      const h = handleFromUrl(url);
      if (h) return h;
    }
    return card.getAttribute('handle');
  }


  /* =============================================================
     MOBILE BOTTOM SHEET
     ============================================================= */

  let overlay, popup;

  function createMobileDOM() {
    if (overlay) return;

    overlay = document.createElement('div');
    overlay.className = 'qa-overlay';
    overlay.setAttribute('aria-hidden', 'true');

    popup = document.createElement('div');
    popup.className = 'qa-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.innerHTML = `
      <div class="qa-popup__handle"></div>
      <div class="qa-popup__title-row">
        <div class="qa-popup__title"></div>
        <div class="qa-popup__price"></div>
      </div>
      <div class="qa-popup__swatches"></div>
      <div class="qa-popup__size-label qa-popup__section-label" style="display:none">Size</div>
      <div class="qa-popup__sizes"></div>
      <button class="qa-popup__atc-btn" disabled type="button">Select a size</button>
      <a class="qa-popup__view-link" href="#">View full details</a>
    `;

    document.body.appendChild(overlay);
    document.body.appendChild(popup);

    overlay.addEventListener('click', closeMobilePopup);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) closeMobilePopup();
    });
  }

  function openMobilePopup(card) {
    createMobileDOM();

    const handle = getActiveHandle(card);
    const mediaLink = card.querySelector('a.product-card__media');
    const productUrl = mediaLink ? mediaLink.getAttribute('href') : `/products/${handle}`;

    const titleEl = card.querySelector('.product-title') || card.querySelector('a[data-instant]');
    popup.querySelector('.qa-popup__title').textContent = titleEl?.textContent?.trim() || '';

    const priceEl = card.querySelector('sale-price') || card.querySelector('.price');
    // Use only text nodes to skip the sr-only 'Sale price' label
    const priceText = priceEl ? Array.from(priceEl.childNodes)
      .filter(n => n.nodeType === Node.TEXT_NODE)
      .map(n => n.textContent.trim())
      .filter(Boolean)
      .join('') : '';
    popup.querySelector('.qa-popup__price').textContent = priceText;

    // Show only the currently selected swatch
    const swatchesContainer = popup.querySelector('.qa-popup__swatches');
    swatchesContainer.innerHTML = '';
    const checkedRadio = card.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const label = card.querySelector(`label[for="${checkedRadio.id}"]`);
      if (label) {
        const wrapper = document.createElement('div');
        wrapper.className = 'qa-popup__swatch-fieldset';
        wrapper.appendChild(label.cloneNode(true));
        swatchesContainer.appendChild(wrapper);
      }
    }

    popup.querySelector('.qa-popup__view-link').href = productUrl;

    const sizesEl   = popup.querySelector('.qa-popup__sizes');
    const atcBtn    = popup.querySelector('.qa-popup__atc-btn');
    const sizeLabel = popup.querySelector('.qa-popup__size-label');

    sizesEl.innerHTML = '<span style="font-size:0.8rem;opacity:0.5;padding:4px 0;display:block">Loading…</span>';
    atcBtn.textContent = 'Select a size';
    atcBtn.disabled = true;
    sizeLabel.style.display = 'none';

    if (atcBtn._qaListener) {
      atcBtn.removeEventListener('click', atcBtn._qaListener);
      delete atcBtn._qaListener;
    }

    overlay.classList.add('is-open');
    popup.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    fetchProduct(handle).then(product => {
      if (!product) {
        sizesEl.innerHTML = '<span style="font-size:0.8rem;color:red;display:block">Could not load options.</span>';
        return;
      }

      const sizeMap = buildSizeMap(product);
      sizesEl.innerHTML = '';

      if (!sizeMap) {
        const variantId = product.variants[0]?.id;
        if (!variantId) return;
        atcBtn.textContent = 'Add to cart';
        atcBtn.disabled = false;
        const listener = () => {
          addToCart(variantId, atcBtn, card).then(() => setTimeout(closeMobilePopup, 1400));
        };
        atcBtn._qaListener = listener;
        atcBtn.addEventListener('click', listener);
        return;
      }

      sizeLabel.style.display = '';
      let selectedVariantId = null;

      const allSizes = getOrderedSizes(product);
      const youthSizes = allSizes.filter(s => /y$/i.test(s.trim()));
      const adultSizes = allSizes.filter(s => !/y$/i.test(s.trim()));

      function renderSizeRow(sizes, container) {
        if (!sizes.length) return;
        const row = document.createElement('div');
        row.className = 'qa-popup__size-row';
        sizes.forEach(size => {
          const entry = sizeMap[size];
          if (!entry) return;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'qa-popup__size-btn' + (entry.available ? '' : ' is-sold-out');
          btn.textContent = size;
          btn.disabled = !entry.available;

          if (entry.available) {
            btn.addEventListener('click', () => {
              sizesEl.querySelectorAll('.qa-popup__size-btn').forEach(b => b.classList.remove('is-selected'));
              btn.classList.add('is-selected');
              selectedVariantId = entry.variantId;
              atcBtn.textContent = 'Add to cart';
              atcBtn.disabled = false;
            });
          }
          row.appendChild(btn);
        });
        container.appendChild(row);
      }

      renderSizeRow(youthSizes, sizesEl);
      renderSizeRow(adultSizes, sizesEl);

      const listener = () => {
        if (!selectedVariantId) return;
        addToCart(selectedVariantId, atcBtn, card).then(() => setTimeout(closeMobilePopup, 1400));
      };
      atcBtn._qaListener = listener;
      atcBtn.addEventListener('click', listener);
    });
  }

  function closeMobilePopup() {
    if (!popup) return;
    overlay.classList.remove('is-open');
    popup.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ── Inject or remove the mobile + button based on availability ───
     The button may not exist in the DOM if the initial product was
     sold out (Prestige gates it with product.available in Liquid).
     On swatch change we inject it if the new sibling is available,
     or remove it if sold out.
  ─────────────────────────────────────────────────────────────────── */
  function getOrCreateQuickAddButton(card) {
    let btn = card.querySelector('.product-card__quick-add-button');
    if (!btn) {
      // Inject a minimal + button matching Prestige's markup
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'product-card__quick-add-button';
      btn.innerHTML = '<span class="sr-only">Choose options</span><svg aria-hidden="true" focusable="false" fill="none" width="12" class="icon icon-plus" viewBox="0 0 12 12"><path d="M6 0v12M0 6h12" stroke="currentColor" stroke-width="1.5"></path></svg>';
      const figure = card.querySelector('.product-card__figure');
      if (figure) figure.appendChild(btn);
    }
    return btn;
  }

  function updateMobileQuickAddButton(card, newHandle) {
    let available = false;
    try {
      const siblings = JSON.parse(card.getAttribute('data-siblings') || '[]');
      const match = siblings.find(s => handleFromUrl(s.url) === newHandle);
      if (match) {
        available = match.available;
      } else {
        // Might be the card's own product — check variant cache
        const cached = variantCache[newHandle];
        available = cached ? cached.variants.some(v => v.available) : true;
      }
    } catch (e) {
      available = true; // fail open
    }

    if (available) {
      getOrCreateQuickAddButton(card);
    } else {
      card.querySelector('.product-card__quick-add-button')?.remove();
    }
  }

  function initMobileQuickAdd() {
    // + button click → open bottom sheet
    document.addEventListener('click', (e) => {
      if (window.innerWidth >= BREAKPOINT) return;
      const btn = e.target.closest('.product-card__quick-add-button');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const card = btn.closest('product-card');
      if (card) openMobilePopup(card);
    }, true);

    // Swatch change → inject or remove + button based on sibling availability
    document.addEventListener('change', (e) => {
      if (window.innerWidth >= BREAKPOINT) return;
      const radio = e.target.closest('input[type="radio"]');
      if (!radio) return;
      const card = radio.closest('product-card');
      if (!card) return;
      const newHandle = handleFromUrl(radio.getAttribute('data-product-url'));
      if (newHandle) updateMobileQuickAddButton(card, newHandle);
    });
  }


  /* =============================================================
     DESKTOP SIZE BAR
     ============================================================= */

  function buildSizeBar(card, product) {
    card.querySelector('.qa-size-bar')?.remove();

    const figure = card.querySelector('.product-card__figure');
    if (!figure) return;

    const bar = document.createElement('div');
    bar.className = 'qa-size-bar';

    const sizeMap = buildSizeMap(product);

    if (!sizeMap) {
      const variantId = product.variants[0]?.id;
      if (!variantId) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'qa-size-bar__size qa-size-bar__size--available';
      btn.textContent = 'Add to cart';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        addToCart(variantId, btn, card);
      });
      bar.appendChild(btn);
      figure.appendChild(bar);
      return;
    }

    getOrderedSizes(product).forEach(size => {
      const entry = sizeMap[size];
      if (!entry) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'qa-size-bar__size ' + (entry.available ? 'qa-size-bar__size--available' : 'qa-size-bar__size--sold-out');
      btn.textContent = size;
      btn.disabled = !entry.available;

      if (entry.available) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          addToCart(entry.variantId, btn, card);
        });
      }
      bar.appendChild(btn);
    });

    figure.appendChild(bar);
  }

  function initDesktopSizeBar() {
    const processed = new WeakSet();

    function attachCard(card) {
      if (processed.has(card)) return;
      processed.add(card);

      const figure = card.querySelector('.product-card__figure');
      if (!figure) return;

      let initialFetched = false;

      figure.addEventListener('mouseenter', () => {
        if (window.innerWidth < BREAKPOINT) return;
        if (initialFetched) return;
        initialFetched = true;
        const handle = getActiveHandle(card);
        fetchProduct(handle).then(product => {
          if (product) buildSizeBar(card, product);
        });
      });

      // Rebuild size bar when color swatch radio changes
      card.addEventListener('change', (e) => {
        const radio = e.target.closest('input[type="radio"]');
        if (!radio) return;
        if (window.innerWidth < BREAKPOINT) return;

        const url = radio.getAttribute('data-product-url');
        const newHandle = handleFromUrl(url);
        if (!newHandle) return;

        initialFetched = true;
        fetchProduct(newHandle).then(product => {
          if (product) buildSizeBar(card, product);
        });
      });
    }

    document.querySelectorAll('product-card').forEach(attachCard);

    new MutationObserver(mutations => {
      mutations.forEach(m => {
        m.addedNodes.forEach(node => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'PRODUCT-CARD') attachCard(node);
          node.querySelectorAll?.('product-card').forEach(attachCard);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }


  /* =============================================================
     INIT
     ============================================================= */

  function init() {
    initMobileQuickAdd();
    initDesktopSizeBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
